import type { BidAskPair } from '@/App/tiles/shared/CommercialMargin/types.ts';
import { Panel } from '@/components/ui/Panel/index.tsx';
import type { IntlMessageId } from '@/types/intl';

import { MarginAmountDisplay } from './MarginAmountDisplay.tsx';
import { MarginSection, type MarginSectionProps } from './MarginSection.tsx';

/**
 * Props for the commercial margin panel
 */
export type CommercialMarginPanelProps = {
  /**
   * Translation key for the panel title
   */
  titleKey: IntlMessageId;

  /**
   * Indicates if the panel is open
   */
  isOpened: boolean;

  /**
   * Indicates if the margin amounts are editable
   */
  isEditable: boolean;

  /**
   * Callback called when toggling the panel
   */
  onToggle: () => void;

  /**
   * Margin sections to display
   */
  sections: MarginSectionProps[];

  /**
   * Margin amount (bid/ask)
   */
  marginAmount: BidAskPair;

  /**
   * Amount currency
   */
  currency: string;

  /**
   * Callback called when toggling the currency
   */
  onCurrencyToggle: () => void;

  /**
   * data-e2e attribute for the panel
   */
  'data-e2e'?: string;
};

/**
 * Commercial margin panel component
 * Displays one or more margin sections (spot/swap) and total amount
 */
export function CommercialMarginPanel(props: CommercialMarginPanelProps) {
  const {
    titleKey,
    isOpened,
    isEditable,
    onToggle,
    sections,
    marginAmount,
    currency,
    onCurrencyToggle,
    'data-e2e': dataE2e = 'commercial-margin-toggle-button',
  } = props;

  return (
    <Panel labelId={titleKey} isOpened={isOpened} onToggle={onToggle} data-e2e={dataE2e}>
      <div
        className="px-2"
        style={{
          display: 'grid',
          gap: '5px',
          gridTemplateColumns: '100px 131px 40px',
          gridTemplateRows: '20px',
        }}
      >
        {/* Margin sections (spot and/or swap) */}
        {sections.map((section) => (
          <MarginSection key={section.type} {...section} isEditable={isEditable} />
        ))}

        {/* Total amount with currency */}
        <MarginAmountDisplay
          amount={marginAmount}
          currency={currency}
          onCurrencyToggle={onCurrencyToggle}
          isEditable={isEditable}
          dataE2ePrefix="commercial-margin"
        />
      </div>
    </Panel>
  );
}
